/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db, auth } from "../../../firebaseConfig";
import ConfirmModal from "../modals/confirmModal";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { createNewViewWithUniqueID, duplicateView } from "../Functions/viewHelpers";

import "./RecentLibrary.css";
import { useNavigate } from "react-router-dom";


function RecentLibrary({ showActions, onLibraryDataLoad }) {
  const [viewIDs, setViewIDs] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [libraryToDelete, setLibraryToDelete] = useState(null);
  const [tempTitles, setTempTitles] = useState({}); // To store unsaved title edits

  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchViews = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found.");
        return;
      }
      const userUID = currentUser.uid; // Get the current user's UID
      // Update the query to filter by createdBy
      const viewsCollectionRef = query(collection(db, "library"), where("createdBy", "==", userUID));

      try {
        const viewsSnapshot = await getDocs(viewsCollectionRef);

        const viewsList = viewsSnapshot.docs.map((doc) => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...(docData ? docData : {}), // Spread only if docData exists
          };
        });

        viewsList.sort((a, b) => {
          const timeA = a.createdAt?.seconds || 0;
          const timeB = b.createdAt?.seconds || 0;
          return timeB - timeA;
        });

        setViewIDs(viewsList);
      } catch (error) {
        console.error("Error fetching library:", error);
      }
    };

    if (!viewIDs) fetchViews();
  }, [viewIDs]);

  const handleDelete = async () => {
    if (libraryToDelete) {
      try {
        await deleteDoc(doc(db, "library", libraryToDelete));
        setViewIDs((prev) => prev.filter((library) => library.id !== libraryToDelete));
        console.log("Project deleted successfully!");
      } catch (error) {
        console.error("Error deleting project:", error);
      }
      setIsModalOpen(false);
    }
  };

  const handleTemporaryTitleChange = (newTempValue, libraryId) => {
    setTempTitles((prevTempTitles) => ({
      ...prevTempTitles,
      [libraryId]: newTempValue,
    }));
  };
  const updateTitleInFirestore = async (newTitle, currentID) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user found.");
      return;
    }
    const docRef = doc(db, "library", currentID);

    try {
      await updateDoc(docRef, { title: newTitle });
      //console.log("Title updated in Firestore.");
    } catch (error) {
      console.error("Error updating title in Firestore:", error);
    }
  };

  const handleInputKeyDown = async (event, libraryId) => {
    const libraryItem = viewIDs.find((lib) => lib.id === libraryId);
    if (!libraryItem) return;

    const originalTitle = libraryItem.title;
    const newTitle = tempTitles[libraryId]; // Current edited value from temp state

    if (event.key === "Enter") {
      // Handles Enter, Meta+Enter, Cmd+Enter, Ctrl+Enter
      event.preventDefault();

      const trimmedNewTitle = newTitle !== undefined ? newTitle.trim() : "";

      // Save if the new title is valid, has changed, and is not empty
      if (newTitle !== undefined && trimmedNewTitle !== "" && trimmedNewTitle !== originalTitle) {
        setViewIDs((prevViewIDs) => prevViewIDs.map((lib) => (lib.id === libraryId ? { ...lib, title: trimmedNewTitle } : lib)));
        await updateTitleInFirestore(trimmedNewTitle, libraryId);
      }

      // Clear temporary title for this item and blur input, regardless of save
      setTempTitles((prevTempTitles) => {
        const updatedTempTitles = { ...prevTempTitles };
        delete updatedTempTitles[libraryId];
        return updatedTempTitles;
      });
      event.target.blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      // Revert to original title by clearing the temporary title
      setTempTitles((prevTempTitles) => {
        const updatedTempTitles = { ...prevTempTitles };
        delete updatedTempTitles[libraryId];
        return updatedTempTitles;
      });
      event.target.blur();
    }
  };

  if (!viewIDs || viewIDs.length === 0) {
    return <div>No recent libraries found.</div>;
  }

  return (
    <div className="library-container">
      <ul className="library-list">
        {viewIDs?.map((library) => {
          const displayTitle = tempTitles[library.id] !== undefined ? tempTitles[library.id] : library.title;
          const libraryContent = library.contents?.content && JSON.parse(library.contents.content);

          return (
            <li className="library-item" key={library.id}>
              <Link
              style={{ display: "flex", width: "100%" }}
                onClick={(e) => {
                  e.preventDefault();
                  // Prevent navigation if the click is on the input itself or if a temp title exists for editing
                  if (e.target.tagName === "INPUT" || tempTitles[library.id] !== undefined) {
                  } else {
                    onLibraryDataLoad(library);
                  }
                }}
              >
                <div style={{ backgroundColor: "var(--Grey2)"}}>
                   <svg className="library-item-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"><path fill="var(--Grey7)" d="M21 2H3a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1M11 20H4v-4h7Zm0-6H4v-4h7Zm9 6h-7v-4h7Zm0-6h-7v-4h7Zm0-6H4V4h16Z"/></svg>
                </div>
                <div className="library-item-content" style={{ display: "flex", flexDirection: "column", width: "100%", gap: "0.5em" }}>

                  <div className="library-item-title" style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "flex-start" }}>
                      
                      <div className="recentTitleInput">
                      <input
                        type="text"
                        name="libraryItem"
                        placeholder="Library Item Name"
                        value={displayTitle || ""}
                        onChange={(e) => handleTemporaryTitleChange(e.target.value, library.id)}
                        onKeyDown={(e) => handleInputKeyDown(e, library.id)}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent Link's onClick from firing
                          e.preventDefault(); // Prevent default click action on input (usually focusing, might help ensure focus is kept)
                        }}
                        style={{ width: "100%", boxSizing: "border-box" }}
                      />
                    </div>
                  </div>
                  <div className="library-item-metadata">
                    {library.createdAt && (
                      <p>Created: {new Date(library.createdAt.seconds * 1000).toLocaleDateString()}</p>
                    )}
                    <p>Type: {libraryContent?.dataSource?.type}</p>
                  </div>
                </div>
              </Link>
              <div>
              {showActions && (
                <button
                  onClick={() => {
                    duplicateView(library.id, "library", navigate);
                  }}
                  className="delBtn"
                >
                  <ContentCopyIcon />
                </button>
              )}

              {showActions && (
                <button
                  onClick={() => {
                    setLibraryToDelete(library.id);
                    setIsModalOpen(true);
                  }}
                  className="delBtn"
                >
                  <DeleteIcon />
                </button>
              )}
              </div>
            </li>
          );
        })}
      </ul>

      <ConfirmModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleDelete} />
    </div>
  );
}

export default RecentLibrary;
